import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { BILLING_MODE, BILLING_MODE_LABEL, COMPANY_SUBSCRIPTION_STATUS, DEFAULT_PAYG_PLAN, DEFAULT_PRICING_PLAN, formatDate } from "../../../constants";
import {
  CompanyTextField,
  CompanyPricingField,
  CompanyInvoiceDayField,
  TabStrip,
  validateCompanyForm,
} from "./CompanyFormParts";
import { TABS, errorsByTab, firstErrorKey, firstTabWithError, focusField } from "./companyFieldTabs";
import { buildChangeSet, formatCurrency, isDirtyByTab } from "./companyChangeSet";
import CompanyReview from "./CompanyReview";

// Pricing edits (and the billing-mode switch) are only allowed in these subscription states.
// Module level so the hooks that read it do not need it as a dependency.
const ALLOWED_PRICING_UPDATE_STATUS = ["trialing", "active", "past_due"];

// updateAdmin refuses these BEFORE it writes anything, so a failure with one of these messages
// means nothing was saved. Any other failure may come after the profile / trial / pricing steps
// (they are written in sequence), so the toast warns that part of the save may have gone through.
const PRE_WRITE_ERROR = /^(Missing required fields|Company not found|Unauthorized|Email already in use)/i;
const PARTIAL_SAVE_NOTE = "Some changes may already have been saved.";

function saveFailureMessage(message, status) {
  const text = message || "Error updating company.";
  const isPlainValidation = [400, 403, 404].includes(status) && PRE_WRITE_ERROR.test(text);
  return isPlainValidation ? text : `${text} ${PARTIAL_SAVE_NOTE}`;
}

// Muted "not billed" line under the PAYG usage table (trial companies only).
function PaygTrialUsageLine({ trialModeEnabled, trialUsage, formatCurrency }) {
  if (!trialModeEnabled || !(Number(trialUsage?.amount) > 0)) return null;
  return (
    <div className="mb-1">
      <small className="text-muted">
        Trial usage: {trialUsage.topoCount} topo{trialUsage.topoCount === 1 ? '' : 's'} · {formatCurrency(trialUsage.amount)} (not billed)
      </small>
    </div>
  );
}

// The system-default PAYG rates: the hardcoded constant, overlaid with the configured block.
function getPaygDefaults(defaultPricing) {
  return { ...DEFAULT_PAYG_PLAN, ...(defaultPricing?.payg || {}) };
}

// "Fill Missing" for a PAYG company: only fields that are actually empty are filled. A 0 is a
// deliberate value here (free / pilot company), so unlike the seat path it is never overwritten.
function fillMissingPaygFields(paygPlan, defaultPricing) {
  const defaults = getPaygDefaults(defaultPricing);
  const filled = { ...(paygPlan || {}) };
  let count = 0;
  ['baseCharge', 'pricePerTopo', 'invoiceDayOfMonth'].forEach((key) => {
    const value = filled[key];
    const isEmpty = value === undefined || value === null || value === '' || Number.isNaN(value);
    if (isEmpty && defaults[key] !== undefined) {
      filled[key] = defaults[key];
      count++;
    }
  });
  return { filled, count };
}

// ---------------------------------------------------------------------------------------------
// Module-level view components. Everything below is prop-driven so the (very large) UpdateCompany
// body stays free of inline component definitions - those remount their inputs on every keystroke
// and used to trip react-hooks/rules-of-hooks.
// ---------------------------------------------------------------------------------------------

// Personal & Basic tab: USER DETAILS.
function PersonalTab({ formData, error, onChange, onPhoneChange, setInputRef }) {
  return (
    <fieldset className="border rounded p-3 mb-3">
      <legend className="w-auto px-2">USER DETAILS</legend>

      <div className="row ml-1">
        <div className="col-md-6">
          <CompanyTextField
            label="Username"
            name="username"
            value={formData.username}
            error={error.username}
            onChange={onChange}
            inputRef={setInputRef('username')}
          />
        </div>
        <div className="col-md-6">
          <CompanyTextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            error={error.email}
            onChange={onChange}
            inputRef={setInputRef('email')}
          />
        </div>
      </div>

      <div className="row ml-1">
        <div className="col-md-12">
          <div className="mb-3">
            <label className="form-label">Phone Number</label>
            <PhoneInput
              ref={setInputRef('phoneNo')}
              placeholder="Enter phone number"
              value={`${formData.countryCode}${formData.phoneNo}`}
              onChange={onPhoneChange}
              defaultCountry="US"
              className={error.phoneNo ? 'is-invalid' : ''}
            />
            {error.phoneNo && <div className="invalid-feedback d-block">{error.phoneNo}</div>}
          </div>
        </div>
      </div>
    </fieldset>
  );
}

// Read-only status card (shown when the company requires a valid payment method). The recovery
// buttons that used to live at the bottom of it are now in SubscriptionActionsCard.
function SubscriptionInfoCard({ data, allowPriceUpdate, currentSubscriptionStatus, restrictionMessage }) {
  return (
    <div className="alert alert-light border mb-3">
      <h6 className="mb-2">
        <i className="fa fa-credit-card mr-2"></i>
        Subscription Information
      </h6>
      <div className="row">
        <div className="col-md-3">
          <small className="text-muted">Current Status:</small>
          <br />
          <span className={`badge ${ALLOWED_PRICING_UPDATE_STATUS.includes(currentSubscriptionStatus)
            ? 'bg-success'
            : 'bg-warning'
            }`}>
            {COMPANY_SUBSCRIPTION_STATUS[currentSubscriptionStatus] || 'Unknown'}
          </span>
        </div>
        <div className="col-md-3">
          <small className="text-muted">Trial Mode:</small>
          <br />
          <span className={`badge ${data.trialModeEnabled ? 'bg-info' : 'bg-secondary'}`}>
            {data.trialModeEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <div className="col-md-3">
          <small className="text-muted">Legacy Status:</small>
          <br />
          <span className={`badge ${data.legacyCompany ? 'bg-warning' : 'bg-secondary'}`}>
            {data.legacyCompany ? 'Legacy Company' : 'Standard Company'}
          </span>
        </div>
        <div className="col-md-3">
          <small className="text-muted">Pricing Updates:</small>
          <br />
          <span className={`badge ${allowPriceUpdate ? 'bg-success' : 'bg-danger'}`}>
            {allowPriceUpdate ? 'Allowed' : 'Blocked'}
          </span>
        </div>
      </div>
      {!allowPriceUpdate && (
        <div className="mt-2">
          <small className="text-muted">
            <i className="fa fa-info-circle mr-2"></i>
            {restrictionMessage}
          </small>
        </div>
      )}
    </div>
  );
}

// BILLING MODEL fieldset: mode badge, PAYG usage table, and the lock alert. The Switch button
// itself lives in SubscriptionActionsCard.
function BillingModelFieldset({ billingMode, paygSummary, trialModeEnabled, allowPriceUpdate, currentSubscriptionStatus }) {
  return (
    <fieldset className="border rounded p-3 mb-3">
      <legend className="w-auto px-2">
        BILLING MODEL
        {/* Explicit size: inside the 1.5rem legend the badge's relative 75% made it oversized. */}
        <span
          className={`badge ${billingMode === BILLING_MODE.PAYG ? 'bg-primary' : 'bg-secondary'} ml-2`}
          style={{ fontSize: '0.75rem' }}
        >
          {BILLING_MODE_LABEL[billingMode]}
        </span>
      </legend>

      {billingMode === BILLING_MODE.PAYG && paygSummary && (
        <div className="mb-3">
          <table className="table table-sm mb-2">
            <tbody>
              <tr>
                <td className="text-muted">Period</td>
                <td className="text-right">{paygSummary.period?.label}</td>
              </tr>
              <tr>
                <td className="text-muted">Topos created</td>
                <td className="text-right">{paygSummary.topoCount}</td>
              </tr>
              <tr>
                <td className="text-muted">Rate</td>
                <td className="text-right">{formatCurrency(paygSummary.pricePerTopo || 0)} per topo</td>
              </tr>
              <tr>
                <td className="text-muted">Usage</td>
                <td className="text-right">{formatCurrency(paygSummary.usageAmount || 0)}</td>
              </tr>
              {Number(paygSummary.baseCharge) > 0 && (
                <tr>
                  <td className="text-muted">Base ({paygSummary.baseChargeLabel})</td>
                  <td className="text-right">{formatCurrency(paygSummary.baseCharge)}</td>
                </tr>
              )}
              <tr className="font-weight-bold border-top">
                <td>Estimated total</td>
                <td className="text-right">{formatCurrency(paygSummary.estimatedTotal || 0)}</td>
              </tr>
            </tbody>
          </table>
          <PaygTrialUsageLine
            trialModeEnabled={trialModeEnabled}
            trialUsage={paygSummary.trialUsage}
            formatCurrency={formatCurrency}
          />
          <small className="text-muted">
            Usage keeps accruing until the period closes, so this is an estimate.{" "}
            {/* The API returns the real date; showing only the day number made the
                operator work it out, and the spec asks for "Invoices on 1 September". */}
            Invoices on{" "}
            {paygSummary.nextInvoiceDate
              ? formatDate(paygSummary.nextInvoiceDate)
              : `day ${paygSummary.invoiceDayOfMonth} of the following month`}
            .
          </small>
        </div>
      )}

      {!allowPriceUpdate ? (
        <div className="alert alert-warning mb-0">
          <i className="fa fa-lock mr-2"></i>
          The billing model cannot be changed while the subscription is
          <strong> {COMPANY_SUBSCRIPTION_STATUS[currentSubscriptionStatus] || currentSubscriptionStatus || 'in this state'}</strong>.
        </div>
      ) : (
        <small className="text-muted">
          Use <strong>Subscription actions</strong> at the bottom of this tab to switch the billing model.
        </small>
      )}
    </fieldset>
  );
}

// The three flags moved here from the old COMPANY SETTINGS fieldset. Keep the `form-check`
// markup: BS4 positions .form-check-input absolutely and relies on it.
function BillingSettingsFieldset({ formData, billingMode, allowPriceUpdate, onChange, onLegacyToggle }) {
  return (
    <fieldset className="border rounded p-3 mb-3">
      <legend className="w-auto px-2">BILLING SETTINGS</legend>

      <div className="row ml-1">
        <div className="col-md-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="validPaymentMethodRequired"
              checked={formData.validPaymentMethodRequired}
              onChange={onChange}
              disabled={billingMode === BILLING_MODE.PAYG}
            />
            <label className="ml-2 form-check-label">
              <i className="fa fa-credit-card mr-2"></i>
              Enable Auto Pay
            </label>
            {billingMode === BILLING_MODE.PAYG && (
              <div className="form-text text-muted">
                Required for Pay As You Go — invoices are charged to the card on file.
              </div>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="trialModeEnabled"
              checked={formData.trialModeEnabled}
              onChange={onChange}
            />
            <label className="ml-2 form-check-label">
              <i className="fa fa-clock mr-2"></i>
              Trial Period
            </label>
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="legacyCompany"
              checked={formData.legacyCompany}
              onChange={onLegacyToggle}
              disabled={!allowPriceUpdate}
            />
            <label className="ml-2 form-check-label">
              <i className="fa fa-star mr-2"></i>
              Legacy Company Status
              <small className="d-block text-muted">
                Grants free Foundation Analysis add-on
              </small>
            </label>
          </div>
        </div>
      </div>
    </fieldset>
  );
}

// One pricing input wired to the shared field: builds the dotted key, the Default / Locked /
// legacy state and the "System default: $X" hint. A plain function (not a component) so it can
// be called from the sheets below without adding a component type.
function pricingFieldProps({ plan, field, label, formData, error, onChange, setInputRef, allowPriceUpdate, defaultPricing, isUsingDefault, extra }) {
  const isPayg = plan === 'paygPlan';
  const fieldKey = `${plan}.${field}`;
  const fieldValue = isPayg ? (formData.paygPlan?.[field] ?? 0) : formData.pricingPlan[field];
  const defaultValue = isPayg ? getPaygDefaults(defaultPricing)[field] : defaultPricing?.[field];
  const isLegacyFree = !isPayg && field === 'foundationAnalysisAddOn' && formData.legacyCompany;
  const showDefaultHint = defaultPricing && defaultValue !== fieldValue && allowPriceUpdate && !isLegacyFree;
  return {
    label,
    name: fieldKey,
    value: fieldValue,
    error: error[fieldKey],
    onChange,
    inputRef: setInputRef(fieldKey),
    isLegacyFree,
    isLocked: !allowPriceUpdate,
    isDefault: isUsingDefault(field, plan),
    defaultHint: showDefaultHint ? `System default: ${formatCurrency(defaultValue)}` : undefined,
    ...extra,
  };
}

function PricingSheet({ billingMode, formData, error, onChange, setInputRef, allowPriceUpdate, defaultPricing, isUsingDefault }) {
  const common = { formData, error, onChange, setInputRef, allowPriceUpdate, defaultPricing, isUsingDefault };

  // A PAYG company has no seat prices to edit - only its per-topo rates. Showing the seat sheet
  // here would let a Super Admin change numbers that are never charged, which is exactly the
  // naming confusion this feature has to avoid.
  if (billingMode === BILLING_MODE.PAYG) {
    return (
      <>
        <div className="alert alert-info">
          <i className="fa fa-bolt mr-2"></i>
          This company is on <strong>Pay As You Go</strong>. Users and add-ons are free
          and unlimited; only the rates below are charged. A price change applies to
          topos created from now on — topos already created keep the price they were
          billed at.
        </div>
        <h6 className="mt-3 mb-2 ml-3">
          <i className="fa fa-calendar-alt mr-2"></i>
          Monthly Base
        </h6>
        <div className="row">
          <CompanyPricingField
            {...pricingFieldProps({ ...common, plan: 'paygPlan', field: 'baseCharge', label: 'Base Price (USD / month)', extra: { hint: 'May be 0' } })}
          />
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Currency</label>
              <select className={`form-control ${!allowPriceUpdate ? 'bg-light' : ''}`} value="usd" disabled readOnly>
                <option value="usd">USD ($)</option>
              </select>
            </div>
          </div>
        </div>

        <h6 className="mt-3 mb-2 ml-3">
          <i className="fa fa-bolt mr-2"></i>
          Per-Topo Pricing
        </h6>
        <div className="row">
          <CompanyPricingField
            {...pricingFieldProps({ ...common, plan: 'paygPlan', field: 'pricePerTopo', label: 'Per Topo Charge (USD)', extra: { hint: 'May be 0 for a free / pilot company' } })}
          />
          <CompanyInvoiceDayField
            value={formData.paygPlan?.invoiceDayOfMonth}
            onChange={onChange}
            inputRef={setInputRef('paygPlan.invoiceDayOfMonth')}
            disabled={!allowPriceUpdate}
            error={error['paygPlan.invoiceDayOfMonth']}
            isDefault={isUsingDefault('invoiceDayOfMonth', 'paygPlan')}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="row">
        <CompanyPricingField
          {...pricingFieldProps({ ...common, plan: 'pricingPlan', field: 'baseCharge', label: 'Base Charge (USD)' })}
        />
        <div className="col-md-6">
          <div className="mb-3">
            <label className="form-label">Currency</label>
            <select
              className={`form-control ${!allowPriceUpdate ? 'bg-light' : ''}`}
              name="pricingPlan.currency"
              value={formData.pricingPlan.currency}
              onChange={onChange}
              disabled={!allowPriceUpdate}
            >
              <option value="usd">USD ($)</option>
            </select>
          </div>
        </div>
      </div>

      <h6 className="mt-3 mb-2 ml-3">
        <i className="fa fa-users mr-2"></i>
        User Type Pricing (USD)
      </h6>
      <div className="row ml-1">
        <CompanyPricingField {...pricingFieldProps({ ...common, plan: 'pricingPlan', field: 'companyUser', label: 'Company User' })} />
        <CompanyPricingField {...pricingFieldProps({ ...common, plan: 'pricingPlan', field: 'readWrite', label: 'Read Write User' })} />
        <CompanyPricingField {...pricingFieldProps({ ...common, plan: 'pricingPlan', field: 'topo', label: 'Topo User (seat)' })} />
        <CompanyPricingField {...pricingFieldProps({ ...common, plan: 'pricingPlan', field: 'readOnly', label: 'Read Only User' })} />
      </div>

      <h6 className="mt-3 mb-2 ml-3">
        <i className="fa fa-puzzle-piece mr-2"></i>
        Add-On Pricing (USD)
      </h6>
      <div className="row ml-1">
        <CompanyPricingField
          {...pricingFieldProps({ ...common, plan: 'pricingPlan', field: 'foundationAnalysisAddOn', label: 'Foundation Analysis Add-On', extra: { isAddOn: true } })}
        />
        <CompanyPricingField
          {...pricingFieldProps({ ...common, plan: 'pricingPlan', field: 'floorLevelAddOn', label: 'Floor Level Add-On', extra: { isAddOn: true } })}
        />
      </div>
    </>
  );
}

// Immediate actions (separate endpoints, NOT part of Save). Disabled while the form has unsaved
// changes: they take effect at once and would otherwise act on a company the form no longer
// describes. Seat recovery buttons never render for PAYG - the backend refuses both for it.
function SubscriptionActionsCard({
  locked, showSwitch, switchLabel, switchingMode, onSwitch,
  showRecovery, showResend, recoveryLoading, onRecovery,
}) {
  if (!showSwitch && !showRecovery) return null;
  return (
    <div className={`cmp-actions-card ${locked ? 'cmp-actions-card--disabled' : ''}`}>
      <h6 className="mb-1">
        <i className="fa fa-wrench mr-2"></i>
        Subscription actions
      </h6>
      <small className="text-muted d-block mb-2">These run immediately and are not part of Save.</small>
      <div className="d-flex flex-wrap">
        {showSwitch && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mr-2 mb-2"
            disabled={locked || switchingMode}
            onClick={onSwitch}
          >
            {switchingMode ? (
              <i className="fa fa-spinner fa-spin mr-1"></i>
            ) : (
              <i className="fa fa-exchange-alt mr-1"></i>
            )}
            {switchLabel}
          </button>
        )}

        {showRecovery && showResend && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mr-2 mb-2"
            disabled={locked || !!recoveryLoading}
            onClick={() =>
              onRecovery({
                key: "resend",
                endpoint: "resend-payment-link",
              })
            }
          >
            {recoveryLoading === "resend" ? (
              <i className="fa fa-spinner fa-spin mr-1"></i>
            ) : (
              <i className="fa fa-envelope mr-1"></i>
            )}
            Resend Payment Link
          </button>
        )}

        {showRecovery && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mr-2 mb-2"
            disabled={locked || !!recoveryLoading}
            onClick={() =>
              onRecovery({
                key: "manual",
                endpoint: "switch-to-manual",
                confirmMessage:
                  "Switch this company to manual billing? The Stripe subscription will be cancelled and the company regains access.",
              })
            }
          >
            {recoveryLoading === "manual" ? (
              <i className="fa fa-spinner fa-spin mr-1"></i>
            ) : (
              <i className="fa fa-hand-holding-dollar mr-1"></i>
            )}
            Mark as Manual Pay
          </button>
        )}
      </div>
      {locked && (
        <div className="cmp-actions-note">
          Save or discard your changes before using these actions - they take effect immediately.
        </div>
      )}
    </div>
  );
}

export default function UpdateCompany({ activeModal, setActiveModal, setUpdateModal, data }) {
  const [formData, setFormData] = useState({
    id: "",
    username: "",
    email: "",
    validPaymentMethodRequired: false,
    trialModeEnabled: true,
    legacyCompany: false,
    pricingPlan: { ...DEFAULT_PRICING_PLAN },
    phoneNo: "",
    countryCode: "",
  });

  const [originalData, setOriginalData] = useState(null);
  const [defaultPricing, setDefaultPricing] = useState(null);
  const [error, setError] = useState({});
  const [disable, setDisable] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [legacyFeatures, setLegacyFeatures] = useState(null);
  const [paygSummary, setPaygSummary] = useState(null);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.PERSONAL);
  // True while the Review screen replaces the tabs in the modal body.
  const [reviewing, setReviewing] = useState(false);
  const billingMode = data?.billingMode === BILLING_MODE.PAYG ? BILLING_MODE.PAYG : BILLING_MODE.SEAT;

  const inputRefs = useRef({});

  const setInputRef = useCallback((name) => (element) => {
    if (element) {
      inputRefs.current[name] = element;
    } else {
      delete inputRefs.current[name];
    }
  }, []);


  // Pricing update restrictions based on subscription status
  const currentSubscriptionStatus = data?.currentSubscription?.status;
  const allowPriceUpdate = useMemo(() => {
    if (!data) return false;

    if (data.validPaymentMethodRequired) {
      // When payment method validation is required, check:
      return ALLOWED_PRICING_UPDATE_STATUS.includes(currentSubscriptionStatus);
    } else {
      // When payment method validation is NOT required:
      return data.trialModeEnabled || !data.validPaymentMethodRequired;
    }
  }, [data, currentSubscriptionStatus]);

  // Get pricing update restriction message
  const getPricingRestrictionMessage = useCallback(() => {
    if (allowPriceUpdate) return null;

    if (data?.validPaymentMethodRequired && !ALLOWED_PRICING_UPDATE_STATUS.includes(currentSubscriptionStatus)) {
      const allowedStatuses = ALLOWED_PRICING_UPDATE_STATUS.map(key => COMPANY_SUBSCRIPTION_STATUS[key]);

      return `Pricing updates are disabled. Subscription status "${currentSubscriptionStatus}" does not allow pricing changes. Allowed statuses: ${allowedStatuses.join(', ')}.`;
    }

    return "Pricing updates are currently disabled for this company.";
  }, [allowPriceUpdate, data, currentSubscriptionStatus]);

  // Merge existing pricing with defaults (existing takes priority)
  const mergePricingWithDefaults = useCallback((existingPricing, defaultPricing, isLegacy = false) => {
    const merged = { ...DEFAULT_PRICING_PLAN };

    // Apply default pricing first if available
    if (defaultPricing) {
      Object.keys(merged).forEach(key => {
        if (defaultPricing[key] !== undefined) {
          merged[key] = defaultPricing[key];
        }
      });
    }

    // Apply existing pricing (takes priority)
    if (existingPricing) {
      Object.keys(merged).forEach(key => {
        if (existingPricing[key] !== undefined) {
          merged[key] = existingPricing[key];
        }
      });
    }

    // Adjust for legacy company
    if (isLegacy) {
      merged.foundationAnalysisAddOn = 0;
    }

    return merged;
  }, []);

  // Load default pricing configuration
  const loadDefaultPricing = useCallback(async () => {
    if (defaultPricing) return defaultPricing;

    setIsLoadingDefaults(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_base_url}api/system-config/default-pricing`, {
        headers: { auth_code: process.env.REACT_APP_AUTH_CODE }
      });

      if (response.data.success && response.data.data) {
        const fetchedPricing = response.data.data;
        setDefaultPricing(fetchedPricing);
        return fetchedPricing;
      } else {
        console.warn('Using fallback pricing - no system config found');
        setDefaultPricing(DEFAULT_PRICING_PLAN);
        return DEFAULT_PRICING_PLAN;
      }
    } catch (error) {
      console.error('Error loading default pricing:', error);
      setDefaultPricing(DEFAULT_PRICING_PLAN);
      return DEFAULT_PRICING_PLAN;
    } finally {
      setIsLoadingDefaults(false);
    }
  }, [defaultPricing]);

  // Load legacy features for the company
  const loadLegacyFeatures = useCallback(() => {
    if (data?.legacyMetadata) {
      const features = {
        foundationAnalysisAddOn: false,
        grandfatheredDate: data.legacyMetadata.grandfatheredDate || null,
        notes: data.legacyMetadata.notes || ''
      };

      if (data.legacyMetadata.legacyFeatures && Array.isArray(data.legacyMetadata.legacyFeatures)) {
        data.legacyMetadata.legacyFeatures.forEach(feature => {
          if (feature.feature === 'foundationAnalysisAddOn') {
            features.foundationAnalysisAddOn = true;
            features.foundationAnalysisGrantedDate = feature.grantedDate;
          }
        });
      }

      setLegacyFeatures(features);
    }
  }, [data]);

  // Initialize form data when modal opens or data changes
  useEffect(() => {
    if (!activeModal || !data) return;

    const initializeForm = async () => {
      try {
        // Load default pricing first
        const defaultPricingData = await loadDefaultPricing();

        // Load legacy features
        loadLegacyFeatures();

        // Merge pricing with priority: existing > default > fallback
        const mergedPricing = mergePricingWithDefaults(data.pricingPlan, defaultPricingData, data.legacyCompany);

        const initialFormData = {
          id: data.id || "",
          username: data.username || "",
          email: data.email || "",
          validPaymentMethodRequired: data.validPaymentMethodRequired ?? false,
          trialModeEnabled: data.trialModeEnabled ?? true,
          legacyCompany: data.legacyCompany ?? false,
          pricingPlan: mergedPricing,
          // Config default first, then the company's own. A SEAT company has an empty
          // paygPlan, so without the config layer the Switch dialog would quote - and
          // set-billing-mode would apply - the hardcoded constant rather than the rate the
          // Super Admin actually configured.
          paygPlan: {
            ...DEFAULT_PAYG_PLAN,
            ...(defaultPricingData?.payg || {}),
            ...(data.paygPlan || {}),
          },
          phoneNo: data.phoneNo || "",
          countryCode: data.countryCode || "",
        };

        setFormData(initialFormData);
        setOriginalData(initialFormData);
        setError({});
        setDisable(false);
        setHasUnsavedChanges(false);
        setActiveTab(TABS.PERSONAL);
        setReviewing(false);
      } catch (error) {
        console.error('Error initializing form:', error);
        toast.error('Error loading company data');
      }
    };

    initializeForm();
  }, [activeModal, data, loadDefaultPricing, mergePricingWithDefaults, loadLegacyFeatures]);

  // Current-period usage, for PAYG companies only. Non-fatal on failure: the panel simply
  // does not render rather than blocking the edit form.
  useEffect(() => {
    if (!activeModal || !data?.id || data?.billingMode !== BILLING_MODE.PAYG) {
      setPaygSummary(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_base_url}api/masterAdmin/payg-usage-summary`,
          { auth_code: process.env.REACT_APP_AUTH_CODE, companyId: data.id },
          { headers: { "Content-Type": "application/json" } }
        );
        if (!cancelled && response.data.success === true) setPaygSummary(response.data.data);
      } catch (err) {
        console.error("Error loading Pay-As-You-Go usage:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [activeModal, data]);

  // Track unsaved changes
  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, originalData]);

  // Handle phone number changes
  const handlePhoneChange = useCallback((value) => {
    // A stale phone error would keep the tab's red dot lit after the number is edited.
    setError(prev => (prev.phoneNo ? { ...prev, phoneNo: '' } : prev));

    if (!value) {
      setFormData(prev => ({
        ...prev,
        countryCode: "",
        phoneNo: "",
      }));
      return;
    }

    try {
      const parsed = parsePhoneNumberWithError(value);
      setFormData(prev => ({
        ...prev,
        countryCode: `+${parsed.countryCallingCode}`,
        phoneNo: parsed.nationalNumber,
      }));
    } catch (err) {
      console.error("Invalid phone number:", err);
    }
  }, []);

  // Handle form input changes
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    let selectionStart, selectionEnd;
    if (['text', 'number', 'email'].includes(type)) {
      selectionStart = e.target.selectionStart;
      selectionEnd = e.target.selectionEnd;
    }

    // Block pricing changes if not allowed - PAYG rates are gated by the same rule.
    if ((name.startsWith('pricingPlan.') || name.startsWith('paygPlan.')) && !allowPriceUpdate) {
      toast.warning('Pricing updates are not allowed for this subscription status');
      return;
    }

    if (name.startsWith('pricingPlan.')) {
      const pricingKey = name.replace('pricingPlan.', '');

      // Don't allow changing foundation analysis pricing for legacy companies
      if (pricingKey === 'foundationAnalysisAddOn' && formData.legacyCompany) {
        toast.warning('Foundation Analysis pricing cannot be changed for legacy companies');
        return;
      }

      setFormData(prev => ({
        ...prev,
        pricingPlan: {
          ...prev.pricingPlan,
          [pricingKey]: type === 'number' ? Math.max(0, Number(value)) : value
        }
      }));
    } else if (name.startsWith('paygPlan.')) {
      // Without this branch the value would be written as a FLAT key
      // (formData['paygPlan.pricePerTopo']) and the nested paygPlan object would never
      // update - the input would appear frozen.
      const paygKey = name.replace('paygPlan.', '');
      setFormData(prev => ({
        ...prev,
        paygPlan: {
          ...prev.paygPlan,
          [paygKey]: type === 'number' || paygKey === 'invoiceDayOfMonth'
            ? Math.max(0, Number(value))
            : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error for this field
    if (error[name]) {
      setError(prev => ({ ...prev, [name]: '' }));
    }

    if (['text', 'number', 'email'].includes(type)) {
      setTimeout(() => {
        const inputEl = inputRefs.current[name];
        if (inputEl) {
          inputEl.focus();
          inputEl.setSelectionRange(selectionStart, selectionEnd);
        }
      }, 0);
    }
  }, [error, allowPriceUpdate, formData.legacyCompany]);

  // Handle legacy company toggle - auto-adjust pricing
  const handleLegacyToggle = useCallback((e) => {
    const isLegacy = e.target.checked;

    if (!allowPriceUpdate && isLegacy !== formData.legacyCompany) {
      toast.warning('Legacy status changes are not allowed for this subscription status');
      return;
    }

    setFormData(prev => ({
      ...prev,
      legacyCompany: isLegacy,
      pricingPlan: {
        ...prev.pricingPlan,
        // Foundation analysis is free for legacy companies
        foundationAnalysisAddOn: isLegacy ? 0 : (defaultPricing?.foundationAnalysisAddOn || DEFAULT_PRICING_PLAN.foundationAnalysisAddOn)
      }
    }));

    if (isLegacy) {
      toast.info('Legacy company status enabled. Foundation Analysis add-on is now FREE.');
    } else {
      toast.info('Legacy company status disabled. Foundation Analysis add-on pricing restored.');
    }
  }, [allowPriceUpdate, formData.legacyCompany, defaultPricing]);

  // Reset to default pricing for missing keys only
  const handleFillMissingWithDefaults = useCallback(async () => {
    if (!allowPriceUpdate) {
      toast.warning('Pricing updates are not allowed for this subscription status');
      return;
    }

    // Pay As You Go companies have no seat pricing: act on the PAYG price sheet only.
    if (billingMode === BILLING_MODE.PAYG) {
      const { filled, count } = fillMissingPaygFields(formData.paygPlan, defaultPricing);
      if (count > 0) {
        setFormData(prev => ({ ...prev, paygPlan: filled }));
        toast.success(`Filled ${count} missing pricing fields with defaults`);
      } else {
        toast.info('No missing pricing fields to fill');
      }
      return;
    }

    if (!defaultPricing) {
      const defaultPricingData = await loadDefaultPricing();
      if (!defaultPricingData) return;
    }

    const currentPricing = formData.pricingPlan;
    const updatedPricing = { ...currentPricing };
    let changesCount = 0;

    // Only fill missing or zero values with defaults
    Object.keys(DEFAULT_PRICING_PLAN).forEach(key => {
      if ((currentPricing[key] === undefined || currentPricing[key] === 0) &&
        defaultPricing[key] !== undefined && defaultPricing[key] > 0) {

        // Don't override foundation analysis for legacy companies
        if (key === 'foundationAnalysisAddOn' && formData.legacyCompany) {
          return;
        }

        updatedPricing[key] = defaultPricing[key];
        changesCount++;
      }
    });

    if (changesCount > 0) {
      setFormData(prev => ({
        ...prev,
        pricingPlan: updatedPricing
      }));
      toast.success(`Filled ${changesCount} missing pricing fields with defaults`);
    } else {
      toast.info('No missing pricing fields to fill');
    }
  }, [formData.pricingPlan, formData.paygPlan, formData.legacyCompany, defaultPricing, loadDefaultPricing, allowPriceUpdate, billingMode]);

  // Reset pricing to original company values
  const handleResetToOriginal = useCallback(() => {
    if (!allowPriceUpdate) {
      toast.warning('Pricing updates are not allowed for this subscription status');
      return;
    }

    if (originalData) {
      if (billingMode === BILLING_MODE.PAYG) {
        // PAYG: restore the original PAYG price sheet; the seat sheet is not in play.
        setFormData(prev => ({
          ...prev,
          paygPlan: { ...originalData.paygPlan }
        }));
        setError(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith('paygPlan.'))));
      } else {
        setFormData(prev => ({
          ...prev,
          pricingPlan: { ...originalData.pricingPlan },
          legacyCompany: originalData.legacyCompany
        }));
      }
      toast.info('Settings reset to original company values');
    }
  }, [originalData, allowPriceUpdate, billingMode]);

  // Form validation. Sets the errors and returns them so the caller can route to the right tab.
  const validate = useCallback(() => {
    const newErrors = validateCompanyForm(formData, billingMode);
    setError(newErrors);
    return newErrors;
  }, [formData, billingMode]);

  // "Review changes": validate, jump to the first tab with an error (and focus the field), or
  // open the Review screen.
  const handleReview = useCallback(() => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fix the validation errors before submitting');
      const tab = firstTabWithError(newErrors);
      if (tab) {
        setActiveTab(tab);
        // The target pane may only be mounting now; focusField defers a frame.
        focusField(inputRefs, firstErrorKey(newErrors, tab));
      }
      return;
    }
    setReviewing(true);
  }, [validate]);

  // The single update-admin request (payload, endpoint and success handling are unchanged).
  // Called from the Review screen, which has already validated.
  const handleSave = useCallback(async () => {
    setDisable(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/update-admin`,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          newUsername: formData.username.trim(),
          newEmail: formData.email.trim(),
          username: originalData?.username || data?.username,
          email: originalData?.email || data?.email,
          id: formData.id,
          validPaymentMethodRequired: formData.validPaymentMethodRequired,
          trialModeEnabled: formData.trialModeEnabled,
          legacyCompany: formData.legacyCompany,
          // PAYG has no seat pricing; the API ignores pricingPlan for it and the merged
          // stale seat plan must not be sent. SEAT payload is unchanged.
          ...(billingMode !== BILLING_MODE.PAYG ? { pricingPlan: formData.pricingPlan } : {}),
          // Only sent for a PAYG company. The API rejects a paygPlan on a seat company
          // rather than storing prices that would silently take effect on a later switch.
          ...(billingMode === BILLING_MODE.PAYG ? { paygPlan: formData.paygPlan } : {}),
          phoneNo: formData.phoneNo,
          countryCode: formData.countryCode,
        }
      );

      if (response.data.success) {
        toast.success("Company updated successfully!");
        setActiveModal();
        setUpdateModal();
      } else {
        // Stay open on the Review screen. The backend writes profile / trial / pricing in
        // sequence, so refresh the list in case an earlier step already went through.
        toast.error(saveFailureMessage(response.data.message));
        setUpdateModal();
      }
    } catch (err) {
      toast.error(saveFailureMessage(err.response?.data?.message, err.response?.status));
      setUpdateModal();
    } finally {
      setDisable(false);
    }
  }, [formData, originalData, data, billingMode, setActiveModal, setUpdateModal]);


  // ============================================================================
  // Subscription recovery actions (resend link / restart / mark manual)
  // ============================================================================
  const [recoveryLoading, setRecoveryLoading] = useState("");

  const companyId = data?.id || data?._id;

  const runRecoveryAction = useCallback(
    async ({ key, endpoint, confirmMessage }) => {
      if (!companyId) {
        toast.error("Company id missing.");
        return;
      }
      if (confirmMessage && !window.confirm(confirmMessage)) return;

      setRecoveryLoading(key);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_base_url}api/masterAdmin/${endpoint}`,
          {
            auth_code: process.env.REACT_APP_AUTH_CODE,
            id: companyId,
          }
        );

        if (response.data.success) {
          toast.success(response.data.message || "Action completed.");

          // For resend, copy the payment link to the clipboard if available.
          const url = response.data?.data?.url || response.data?.data?.paymentLink;
          if (url) {
            try {
              await navigator.clipboard.writeText(url);
              toast.info("Payment link copied to clipboard.");
            } catch (clipErr) {
              console.warn("Clipboard copy failed:", clipErr);
            }
          }

          // Restart / switch-to-manual change company state — close & refresh list.
          if (key !== "resend") {
            setActiveModal();
            setUpdateModal();
          }
        } else {
          toast.error(response.data.message);
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Action failed.");
      } finally {
        setRecoveryLoading("");
      }
    },
    [companyId, setActiveModal, setUpdateModal]
  );

  // Block is already gated by validPaymentMethodRequired; treat anything that is not
  // active/trialing (including a nulled subscription after expiry) as recoverable.
  const isBrokenSubscription = !["active", "trialing"].includes(
    currentSubscriptionStatus
  );
  const canRestart =
    !currentSubscriptionStatus ||
    ["incomplete_expired", "canceled"].includes(currentSubscriptionStatus);

  // The plan actually used if this company is switched to Pay-As-You-Go right now.
  //
  // `formData.paygPlan` cannot be used directly here: for a company that has never been
  // PAYG, `data.paygPlan` (and therefore formData.paygPlan) is just the untouched
  // Mongoose schema default ({baseCharge:0, pricePerTopo:0, ...}) - never a real price -
  // and must not be shown or sent as though it were one. This mirrors the same
  // missing-or-zero -> global-default rule the backend now enforces
  // (services/billing-mode.service.js switchToPayg), so the confirmation dialog shows
  // the price that will actually be applied, not a stale $0.00.
  //
  // A genuinely positive value already in formData.paygPlan (e.g. a real rate from an
  // earlier stint on PAYG, or one the Super Admin just typed into the rate sheet while
  // already on PAYG) is still honored - only a missing/zero value is replaced.
  const effectivePaygPlanForSwitch = useMemo(() => {
    const defaults = getPaygDefaults(defaultPricing);
    const current = formData.paygPlan || {};
    const pick = (key) => (Number(current[key]) > 0 ? Number(current[key]) : defaults[key]);

    return {
      baseCharge: pick('baseCharge'),
      pricePerTopo: pick('pricePerTopo'),
      currency: current.currency || defaults.currency,
      invoiceDayOfMonth: current.invoiceDayOfMonth || defaults.invoiceDayOfMonth,
    };
  }, [formData.paygPlan, defaultPricing]);

  /**
   * Move this company between Seat and Pay-As-You-Go billing.
   *
   * The confirmation spells out the consequences because none of them are obvious from the
   * button - especially in the Seat direction, where every user silently loses Foundation
   * Analysis and Floor Level until they are purchased per user.
   */
  const changeBillingMode = useCallback(async () => {
    const target = billingMode === BILLING_MODE.PAYG ? BILLING_MODE.SEAT : BILLING_MODE.PAYG;

    const outstanding =
      paygSummary && paygSummary.topoCount
        ? `${paygSummary.topoCount} topo${paygSummary.topoCount === 1 ? '' : 's'}, ` +
          `$${(paygSummary.usageAmount || 0).toFixed(2)}`
        : 'no usage yet';

    const message =
      target === BILLING_MODE.PAYG
        ? [
            `Switch to ${BILLING_MODE_LABEL.PAYG}?`,
            '',
            '\u2022 The Stripe subscription will be CANCELLED immediately.',
            '\u2022 The company keeps the remainder of the month it has already paid for.',
            `\u2022 Topos created from now on are billed at $${Number(effectivePaygPlanForSwitch.pricePerTopo || 0).toFixed(2)} each.`,
            '\u2022 Users and add-ons become free and unlimited.',
            '\u2022 A card must already be on file.',
          ].join('\n')
        : [
            `Switch to ${BILLING_MODE_LABEL.SEAT} billing?`,
            '',
            `\u2022 Topo usage so far this period (${outstanding}) will be INVOICED NOW.`,
            '\u2022 A new subscription will be created for the company\u2019s current users.',
            '\u2022 WARNING: Foundation Analysis and Floor Level will be REMOVED from all',
            '  users until purchased per user.',
          ].join('\n');

    if (!window.confirm(message)) return;

    setSwitchingMode(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_base_url}api/masterAdmin/set-billing-mode`,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          companyId: data?.id,
          billingMode: target,
          paygPlan: target === BILLING_MODE.PAYG ? effectivePaygPlanForSwitch : undefined,
          masterAdminId: sessionStorage.getItem("TES_Admin_ID"),
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        toast.success(response.data.message || `Switched to ${BILLING_MODE_LABEL[target]}.`);
        setActiveModal();
        setUpdateModal();
      } else {
        const step = response.data?.data?.step;
        toast.error(`${response.data.message}${step ? ` (failed at: ${step})` : ''}`);
      }
    } catch (err) {
      // A refusal comes back as HTTP 400, which axios REJECTS - so the step lives here, not
      // in the success branch above. Reading it only there made the whole "names the step it
      // failed at" contract dead code.
      const data = err.response?.data;
      const step = data?.data?.step;
      toast.error(
        `${data?.message || "Could not change the billing mode."}${step ? ` (failed at: ${step})` : ''}`
      );
    } finally {
      setSwitchingMode(false);
    }
  }, [billingMode, paygSummary, effectivePaygPlanForSwitch, data, setActiveModal, setUpdateModal]);

  // Handle modal close with unsaved changes warning
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmClose) return;
    }
    setActiveModal();
  }, [hasUnsavedChanges, setActiveModal]);

  // Check if field uses default value
  // `plan` picks the price sheet: pricingPlan (seat, defaults at the config root) or paygPlan
  // (defaults in the config's `payg` block).
  const isUsingDefault = useCallback((field, plan = 'pricingPlan') => {
    const currentValue = formData[plan]?.[field];
    const originalValue = originalData?.[plan]?.[field];
    const defaultValue = plan === 'paygPlan' ? getPaygDefaults(defaultPricing)[field] : defaultPricing?.[field];

    return currentValue === defaultValue && originalValue !== defaultValue;
  }, [formData, originalData, defaultPricing]);

  // Tab dots: red when the tab holds a validation error, amber when it holds a change.
  const tabs = useMemo(() => {
    const errs = errorsByTab(error);
    const dirty = originalData ? isDirtyByTab(originalData, formData) : {};
    return [
      { key: TABS.PERSONAL, label: 'Personal & Basic', icon: 'fa fa-user', hasError: errs[TABS.PERSONAL], isModified: !!dirty[TABS.PERSONAL] },
      { key: TABS.BILLING, label: 'Pricing & Billing', icon: 'fa fa-dollar-sign', hasError: errs[TABS.BILLING], isModified: !!dirty[TABS.BILLING] },
    ];
  }, [error, originalData, formData]);

  // Review screen content. Client-side on purpose (see companyChangeSet.js). `data` is the row
  // from admins-list: `currentSubscription` is populated there, so stripeSubscriptionId / status
  // are on it, and stripeCustomerId is a plain admin field.
  const hasStripeSubscription = !!data?.currentSubscription?.stripeSubscriptionId;
  const hasStripeCustomer = !!data?.stripeCustomerId;
  const changeSet = useMemo(() => {
    if (!reviewing || !originalData) return null;
    return buildChangeSet(originalData, formData, {
      billingMode,
      hasStripeSubscription,
      hasStripeCustomer,
      status: currentSubscriptionStatus,
    });
  }, [reviewing, originalData, formData, billingMode, hasStripeSubscription, hasStripeCustomer, currentSubscriptionStatus]);

  if (!data) {
    return null;
  }

  const switchLabel = `Switch to ${billingMode === BILLING_MODE.PAYG ? BILLING_MODE_LABEL.SEAT : BILLING_MODE_LABEL.PAYG}`;
  // The seat recovery buttons only make sense for a seat, auto-pay company with a broken
  // subscription; the backend refuses Resend / Mark as Manual Pay for PAYG, so never offer them.
  const showRecovery = billingMode !== BILLING_MODE.PAYG && !!data.validPaymentMethodRequired && isBrokenSubscription;
  const showResend = !!currentSubscriptionStatus && !canRestart;

  return (
    <Modal show={activeModal} onHide={handleClose} size="xl" backdrop="static" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-edit mr-2"></i>
          Edit Company: {data.username}
          {formData.legacyCompany && (
            <span className="badge bg-warning ml-2">
              <i className="fa fa-star mr-2"></i>Legacy Company
            </span>
          )}
          {reviewing ? (
            <span className="badge bg-primary ml-2 mr-2">Review changes</span>
          ) : (
            hasUnsavedChanges && (
              <span className="badge bg-info ml-2 mr-2">Unsaved Changes</span>
            )
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingDefaults && (
          <div className="alert alert-info">
            <i className="fa fa-spinner fa-spin mr-2"></i>
            Loading default pricing configuration...
          </div>
        )}

        {reviewing ? (
          <CompanyReview
            changeSet={changeSet}
            onBack={() => setReviewing(false)}
            onConfirm={handleSave}
            saving={disable}
          />
        ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            <TabStrip tabs={tabs} active={activeTab} onChange={setActiveTab} />

            {activeTab === TABS.PERSONAL && (
              <div className="cmp-tab-pane">
                <PersonalTab
                  formData={formData}
                  error={error}
                  onChange={handleInputChange}
                  onPhoneChange={handlePhoneChange}
                  setInputRef={setInputRef}
                />
              </div>
            )}

            {activeTab === TABS.BILLING && (
              <div className="cmp-tab-pane">
                {/* Subscription Status Info */}
                {data.validPaymentMethodRequired && (
                  <SubscriptionInfoCard
                    data={data}
                    allowPriceUpdate={allowPriceUpdate}
                    currentSubscriptionStatus={currentSubscriptionStatus}
                    restrictionMessage={getPricingRestrictionMessage()}
                  />
                )}

                {/* Pay-As-You-Go: current period usage */}
                <BillingModelFieldset
                  billingMode={billingMode}
                  paygSummary={paygSummary}
                  trialModeEnabled={formData.trialModeEnabled}
                  allowPriceUpdate={allowPriceUpdate}
                  currentSubscriptionStatus={currentSubscriptionStatus}
                />

                <BillingSettingsFieldset
                  formData={formData}
                  billingMode={billingMode}
                  allowPriceUpdate={allowPriceUpdate}
                  onChange={handleInputChange}
                  onLegacyToggle={handleLegacyToggle}
                />

                {/* Pricing Configuration */}
                <fieldset className={`border rounded p-3 mb-3 ${!allowPriceUpdate ? 'bg-light' : ''}`}>
                  <legend className="w-auto px-2">
                    PRICING CONFIGURATION
                    {!allowPriceUpdate && (
                      <span className="badge bg-danger ml-2">
                        <i className="fa fa-lock mr-2"></i>Updates Disabled
                      </span>
                    )}
                    {allowPriceUpdate && (
                      <div className="btn-group ml-2" role="group">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary mr-2"
                          onClick={handleFillMissingWithDefaults}
                          title="Fill missing values with system defaults"
                          disabled={!defaultPricing}
                        >
                          <i className="fa fa-magic"></i> Fill Missing
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleResetToOriginal}
                          title="Reset to original company values"
                          disabled={!originalData}
                        >
                          <i className="fa fa-undo"></i> Reset Original
                        </button>
                      </div>
                    )}
                  </legend>

                  {!allowPriceUpdate && (
                    <div className="alert alert-warning mb-3">
                      <i className="fa fa-exclamation-triangle mr-2"></i>
                      <strong>Pricing Updates Disabled:</strong> {getPricingRestrictionMessage()}
                    </div>
                  )}

                  <PricingSheet
                    billingMode={billingMode}
                    formData={formData}
                    error={error}
                    onChange={handleInputChange}
                    setInputRef={setInputRef}
                    allowPriceUpdate={allowPriceUpdate}
                    defaultPricing={defaultPricing}
                    isUsingDefault={isUsingDefault}
                  />
                </fieldset>

                <SubscriptionActionsCard
                  locked={hasUnsavedChanges}
                  showSwitch={allowPriceUpdate}
                  switchLabel={switchLabel}
                  switchingMode={switchingMode}
                  onSwitch={changeBillingMode}
                  showRecovery={showRecovery}
                  showResend={showResend}
                  recoveryLoading={recoveryLoading}
                  onRecovery={runRecoveryAction}
                />
              </div>
            )}
          </form>
        )}
      </Modal.Body>

      {!reviewing && (
        <Modal.Footer>
          <div className="mr-auto">
            {hasUnsavedChanges && (
              <small className="text-warning">
                <i className="fa fa-exclamation-triangle mr-1"></i>
                You have unsaved changes
              </small>
            )}
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={disable}
          >
            Cancel
          </button>
          {/* btn-outline-*: the global .btn-primary override in PricingConfig.css changes its size. */}
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleReview}
            disabled={disable || isLoadingDefaults || !hasUnsavedChanges}
          >
            <i className="fa fa-clipboard-check mr-2"></i>
            Review changes
          </button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
